import { UserDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { Button, DialogActions, Grid, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { Alert, AlertTitle } from '@mui/material';
import { parseErrorMessage } from '../../../../../core/api/api.context';
import { Checkbox } from '../../../../../ui/components/pure/checkbox/checkbox.component';
import { Dropdown } from '../../../../../ui/components/pure/dropdown/dropdown.component';
import { FormControl } from '../../../../../ui/components/pure/form-control/form-control.component';
import { TwoPaneModalContent } from '../../../../../ui/components/pure/modal-content/two-pane-modal-content.component';
import { Modal } from '../../../../../ui/components/pure/modal/modal.component';
import { TextButton } from '../../../../../ui/components/pure/text-button/text-button.component';
import { TextField } from '../../../../../ui/components/pure/text-field/text-field.component';
import { MobileField } from '../../../../../ui/components/pure/mobile-field/mobile-field.component';
import { useEditUserAction } from './edit-user-action.hook';
import { PadLock } from '../../../../../ui/components/pure/icons/PadLock.icon';
import { useQueryWhoAmI } from '../../../../../core/queries/directory.queries';
import { FreeSoloAutocomplete } from '../../../../../ui/components/pure/autocomplete/freesolo-autocomplete.component';
import { useUserAddress } from '../../hooks/use-user-address.hook';

export interface EditUserActionProps {
  userDto?: UserDto;
  projectID: string;
  childProjectID?: string;
  onAbort(): void;
  onSuccess(): void;
}

export function CreateOrEditUserModal(props: EditUserActionProps) {
  const { state, handlers, form, currentUserType, groupOptions, projectOptions } = useEditUserAction(props);

  const { userDto } = props;
  const { watch, handleSubmit, control, formConfig, mobileCountry } = form;

  const loggedInAccount = useQueryWhoAmI();
  const isTextFieldDisabled =
    userDto?.managedByAccount?.id && userDto?.managedByAccount.id !== loggedInAccount.data?.account.id ? true : false;
  const isProjectSelected = watch('projectID') || userDto?.projectId ? true : false;

  const theme = useTheme();
  const isSM = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Modal
      title={`${userDto ? 'Edit' : 'Create'} ${currentUserType}`}
      open={true}
      loading={state.loading || state.saving || loggedInAccount.isLoading}
      maxWidth="md"
    >
      {userDto?.managedByAccount ? (
        <Tooltip title={isSM ? `Managed by ${userDto.managedByAccount.name}` : ''}>
          <Button
            color="primary"
            variant="outlined"
            style={{ position: 'absolute', right: '10px', top: '10px', color: '#34AEA7', borderColor: '#34AEA7' }}
            disabled
          >
            <PadLock style={{ height: '20px', color: '#34AEA7', width: '15px', marginRight: '10px' }} />
            <span>Managed{isSM ? null : ` by ${userDto.managedByAccount.name}`}</span>
          </Button>
        </Tooltip>
      ) : null}
      <form noValidate autoComplete="off" onSubmit={handleSubmit(handlers.onSubmit)}>
        <TwoPaneModalContent
          leftDesktopWidth={7}
          rigthDesktopWidth={5}
          flexibleWidthPane="right"
          leftContent={() => (
            <Grid container sx={{ flexDirection: 'column' }}>
              <Grid
                container
                spacing={1}
                style={{ borderBottom: '1px solid #EBEDF2', padding: '0 24px 8px', width: '100%', margin: 0 }}
              >
                <Grid container spacing={2}>
                  <Grid>
                    <FormControl
                      control={control}
                      name={formConfig.firstName.name}
                      required={formConfig.firstName.required}
                      rules={{ validate: formConfig.firstName.validate }}
                      Component={TextField}
                      ComponentProps={{
                        textFieldVariant: 'floating' as const,
                        placeholder: formConfig.firstName.label,
                        style: { width: '180px' },
                        disabled: isTextFieldDisabled
                      }}
                    />
                  </Grid>
                  <Grid>
                    <FormControl
                      control={control}
                      name={formConfig.lastName.name}
                      required={formConfig.lastName.required}
                      rules={{
                        validate: formConfig.lastName.validate
                      }}
                      Component={TextField}
                      ComponentProps={{
                        textFieldVariant: 'floating' as const,
                        placeholder: formConfig.lastName.label,
                        style: { width: '180px' },
                        disabled: isTextFieldDisabled
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid>
                    <FormControl
                      control={control}
                      name={formConfig.preferredName.name}
                      required={formConfig.preferredName.required}
                      rules={{
                        validate: formConfig.preferredName.validate
                      }}
                      Component={TextField}
                      ComponentProps={{
                        textFieldVariant: 'floating' as const,
                        placeholder: formConfig.preferredName.label,
                        style: { width: '180px' },
                        disabled: isTextFieldDisabled
                      }}
                    />
                  </Grid>
                  {formConfig.maidenName && (
                    <Grid>
                      <FormControl
                        control={control}
                        name={formConfig.maidenName.name}
                        required={formConfig.maidenName.required}
                        rules={{
                          validate: formConfig.maidenName.validate
                        }}
                        Component={TextField}
                        ComponentProps={{
                          textFieldVariant: 'floating' as const,
                          placeholder: formConfig.maidenName.label,
                          style: { width: '180px' },
                          disabled: isTextFieldDisabled
                        }}
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Grid
                container
                spacing={1}
                style={{ borderBottom: '1px solid #EBEDF2', padding: '0 24px 8px', width: '100%', margin: 0 }}
              >
                <Grid container spacing={2}>
                  {!userDto && (
                    <Grid>
                      <FormControl
                        control={control}
                        name={formConfig.projectID.name}
                        required={formConfig.projectID.required}
                        rules={{
                          validate: formConfig.projectID.validate
                        }}
                        ComponentProps={{
                          style: { width: 180 },
                          dropdownVariant: 'floating' as const,
                          placeholder: formConfig.projectID.label,
                          menuItems: projectOptions
                        }}
                        Component={Dropdown}
                      />
                    </Grid>
                  )}

                  <Grid>
                    <FreeSoloAutocomplete
                      fullWidth
                      defaultValue={userDto?.groupName}
                      control={control as any}
                      name={formConfig.groupName.name}
                      required={formConfig.groupName.required}
                      label={formConfig.groupName.label}
                      rules={{
                        validate: formConfig.groupName.validate
                      }}
                      disabled={isTextFieldDisabled || !isProjectSelected}
                      // groupOptions should be sting[] for freeSolo to work
                      options={groupOptions}
                      inputStyles={{
                        width: 180
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid
                container
                spacing={1}
                style={{ borderBottom: '1px solid #EBEDF2', padding: '0 24px 8px', width: '100%', margin: 0 }}
              >
                <Grid size={12}>
                  <FormControl
                    control={control}
                    name={formConfig.email.name}
                    required={formConfig.email.required}
                    rules={{
                      validate: formConfig.email.validate
                    }}
                    Component={TextField}
                    ComponentProps={{
                      type: 'email',
                      textFieldVariant: 'floating' as const,
                      placeholder: formConfig.email.label,
                      style: { width: '100%', maxWidth: '316px' },
                      disabled: isTextFieldDisabled
                    }}
                  />
                </Grid>
                <Grid container>
                  <Grid size={12}>
                    <FormControl
                      control={control}
                      name={formConfig.mobile.name}
                      required={formConfig.mobile.required}
                      rules={{
                        validate: formConfig.mobile.validate
                      }}
                      Component={MobileField}
                      ComponentProps={{
                        country: mobileCountry,
                        textFieldVariant: 'floating' as const,
                        placeholder: formConfig.mobile.label,
                        style: { width: '100%', maxWidth: '316px' },
                        disabled: isTextFieldDisabled
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid container spacing={1} style={{ padding: '0 24px', width: '100%', margin: 0 }}>
                <Grid container spacing={2}>
                  <Grid>
                    <FormControl
                      control={control}
                      name={formConfig.externalID.name}
                      required={formConfig.externalID.required}
                      rules={{
                        validate: formConfig.externalID.validate
                      }}
                      Component={TextField}
                      ComponentProps={{
                        textFieldVariant: 'floating' as const,
                        placeholder: formConfig.externalID.label,
                        style: { width: '180px' },
                        disabled: isTextFieldDisabled
                      }}
                    />
                  </Grid>
                  <Grid>
                    <Grid container sx={{ flexDirection: 'column' }} spacing={0}>
                      <Grid>
                        <Checkbox
                          control={control as any}
                          name={formConfig.prefersLowerLockers.name}
                          label={formConfig.prefersLowerLockers.label}
                          required={formConfig.prefersLowerLockers.required}
                        />
                      </Grid>
                      <Grid>
                        <Checkbox
                          control={control as any}
                          name={formConfig.prefersAudioPrompts.name}
                          label={formConfig.prefersAudioPrompts.label}
                          required={formConfig.prefersAudioPrompts.required}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
          rightContent={() => {
            return (
              <Grid container style={{ padding: '8px 16px' }}>
                <Grid>
                  <Typography gutterBottom>ADDRESS:</Typography>
                  <p style={{ lineHeight: 1.5 }}>
                    <strong>
                      <CustomerName
                        firstName={watch('firstName')}
                        lastName={watch('lastName')}
                        preferredName={watch('preferredName')}
                      />
                    </strong>
                    {watch('maidenName') && (
                      <>
                        <br />
                        <strong>(nee {watch('maidenName')})</strong>
                      </>
                    )}
                  </p>
                  <p style={{ lineHeight: 1.5 }}>
                    <UserAddress projectID={watch('projectID')} groupName={watch('groupName')} />
                  </p>
                </Grid>
                {state.error && (
                  <Grid>
                    <Alert variant="outlined" severity="error">
                      <AlertTitle>Something went wrong</AlertTitle>
                      {parseErrorMessage(state.error.message)}
                    </Alert>
                  </Grid>
                )}
              </Grid>
            );
          }}
        />
        <DialogActions style={{ borderTop: '1px solid #EBEDF2', padding: '8px 24px' }}>
          <TextButton color="secondary" variant="outlined" onClick={handlers.onCancel}>
            Cancel
          </TextButton>
          <TextButton color="primary" variant="outlined" type="submit">
            Save
          </TextButton>
        </DialogActions>
      </form>
    </Modal>
  );
}

function UserAddress({ projectID, groupName }: { projectID?: string; groupName?: string }) {
  const { line1, line2, country } = useUserAddress(projectID, groupName);

  return (
    <>
      {line1}
      <br />
      {line2}
      <br />
      <span style={{ textTransform: 'uppercase' }}>{country}</span>
    </>
  );
}

function CustomerName({
  firstName,
  lastName,
  preferredName
}: {
  firstName?: string;
  lastName?: string;
  preferredName?: string;
}) {
  return (
    <>
      {firstName ? firstName + ' ' : ''}
      {preferredName ? `(${preferredName}) ` : ''}
      {lastName || ''}
    </>
  );
}
